import { useEffect, useRef } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, Heading2, Undo2, Redo2 } from "lucide-react";

/**
 * Minimal contentEditable WYSIWYG.
 * - Stores its value as HTML in `value` and emits onChange(html).
 * - Toolbar buttons: bold/italic/underline, h2, bullet list, numbered list, link,
 *   undo/redo. Uses document.execCommand which is deprecated but still works
 *   in every browser we support and avoids pulling in a 100KB editor dependency.
 * - The page sanitizes nothing — admins are trusted authors. Output is rendered
 *   with dangerouslySetInnerHTML inside the WhatsNewCard.
 */
export const RichTextEditor = ({ value, onChange, placeholder = "Write here…", minHeight = 180 }) => {
  const ref = useRef(null);

  // Initialize content once (uncontrolled after that — typing wouldn't survive
  // a re-render from controlled value)
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    onChange?.(ref.current?.innerHTML || "");
  };

  const onLink = () => {
    const url = window.prompt("Enter URL (include https://)");
    if (url) exec("createLink", url);
  };

  const Btn = ({ icon: Icon, label, onClick, testid }) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={testid}
      className="p-1.5 rounded hover:bg-secondary text-foreground transition-colors"
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="border rounded-md overflow-hidden" data-testid="rich-text-editor">
      <div className="flex items-center gap-0.5 px-2 py-1 border-b bg-secondary/40">
        <Btn icon={Bold}        label="Bold (Ctrl+B)"    onClick={() => exec("bold")}            testid="rte-bold" />
        <Btn icon={Italic}      label="Italic (Ctrl+I)"  onClick={() => exec("italic")}          testid="rte-italic" />
        <Btn icon={Underline}   label="Underline (Ctrl+U)" onClick={() => exec("underline")}     testid="rte-underline" />
        <div className="w-px h-5 bg-border mx-1" />
        <Btn icon={Heading2}    label="Heading"          onClick={() => exec("formatBlock", "h2")} testid="rte-h2" />
        <Btn icon={List}        label="Bullet list"      onClick={() => exec("insertUnorderedList")} testid="rte-ul" />
        <Btn icon={ListOrdered} label="Numbered list"    onClick={() => exec("insertOrderedList")} testid="rte-ol" />
        <Btn icon={LinkIcon}    label="Insert link"      onClick={onLink}                        testid="rte-link" />
        <div className="w-px h-5 bg-border mx-1" />
        <Btn icon={Undo2}       label="Undo (Ctrl+Z)"    onClick={() => exec("undo")}            testid="rte-undo" />
        <Btn icon={Redo2}       label="Redo (Ctrl+Shift+Z)" onClick={() => exec("redo")}         testid="rte-redo" />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange?.(e.currentTarget.innerHTML)}
        onPaste={(e) => {
          // When the clipboard contains HTML, honor it (so admins can paste
          // a formatted block from elsewhere). When it's plain text, paste
          // as text so we don't accidentally treat raw <p>... markup as
          // literal characters with escaped angle brackets.
          const data = e.clipboardData;
          if (!data) return;
          const html = data.getData("text/html");
          const text = data.getData("text/plain");
          // If the text looks like HTML source someone typed (e.g. starts
          // with "<" and contains a closing tag), insert it as real HTML.
          const looksLikeHtml = /^\s*<\w+[^>]*>[\s\S]*<\/\w+>\s*$/m.test(text || "");
          if (html || looksLikeHtml) {
            e.preventDefault();
            const content = html || text;
            document.execCommand("insertHTML", false, content);
            onChange?.(ref.current?.innerHTML || "");
          }
          // else fall through to default plain-text paste
        }}
        data-placeholder={placeholder}
        data-testid="rich-text-editor-content"
        className="prose prose-sm max-w-none px-3 py-2 focus:outline-none [&[data-placeholder]:empty]:before:content-[attr(data-placeholder)] [&[data-placeholder]:empty]:before:text-muted-foreground"
        style={{ minHeight }}
      />
    </div>
  );
};

export default RichTextEditor;
